/**
 * AnalyticsDashboard — Compact KPI dashboard for the Admin panel.
 *
 * Consumes GET /api/admin/analytics/summary. No third-party chart lib:
 * we render lightweight CSS bar charts to keep bundle size low.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, TrendingUp, Users, Euro, Target, Mail, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (cents) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    .format((cents || 0) / 100);

const STATUS_LABELS = {
  nouvelle_demande: 'Nouvelle',
  a_analyser: 'À analyser',
  contact_a_effectuer: 'Contact à faire',
  rdv_programme: 'RDV',
  proposition_envoyee: 'Proposition',
  client: 'Client',
  non_conclu: 'Non conclu',
  a_relancer: 'À relancer',
};

const KpiCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div
    className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-4 flex flex-col gap-1"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-[#888680] text-xs uppercase tracking-wide">
      <Icon size={14} className="text-[#d9fb06]" />
      {label}
    </div>
    <div className="text-2xl font-black text-[#d9fb06]">{value}</div>
    {sub && <div className="text-[#888680] text-xs">{sub}</div>}
  </div>
);

const BarRow = ({ label, value, max, valueFmt }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="text-xs">
      <div className="flex justify-between mb-1">
        <span className="text-[#dfddd6] truncate">{label}</span>
        <span className="text-[#d9fb06] font-bold ml-2 flex-shrink-0">{valueFmt ? valueFmt(value) : value}</span>
      </div>
      <div className="h-2 bg-[#1a1c1b] rounded overflow-hidden">
        <div className="h-full bg-[#d9fb06]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const MonthlyChart = ({ data }) => {
  const maxP = Math.max(1, ...data.map((d) => d.prospects));
  const maxR = Math.max(1, ...data.map((d) => d.revenue_cents));
  return (
    <div className="grid grid-cols-6 gap-2 h-40 items-end mt-3">
      {data.map((d) => {
        const hP = maxP ? Math.max(4, (d.prospects / maxP) * 100) : 0;
        const hR = maxR ? Math.max(4, (d.revenue_cents / maxR) * 100) : 0;
        return (
          <div key={d.month} className="flex flex-col items-center gap-1 h-full justify-end">
            <div className="w-full flex gap-1 items-end h-32">
              <div className="flex-1 bg-[#d9fb06] rounded-t" style={{ height: `${hP}%` }}
                title={`${d.prospects} prospect(s)`} />
              <div className="flex-1 bg-[#3f4816] rounded-t" style={{ height: `${hR}%` }}
                title={`${fmtEUR(d.revenue_cents)}`} />
            </div>
            <div className="text-[10px] text-[#888680] font-mono">{d.month.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
};

const Sparkline = ({ series }) => {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div className="flex gap-[2px] h-16 items-end mt-2" data-testid="prospects-sparkline">
      {series.map((s) => {
        const h = Math.max(3, Math.round((s.count / max) * 100));
        return (
          <div key={s.date} className="flex-1 bg-[#d9fb06]/70 hover:bg-[#d9fb06] transition-colors rounded-t"
            style={{ height: `${h}%` }} title={`${s.date} — ${s.count} demande(s)`} />
        );
      })}
    </div>
  );
};

export default function AnalyticsDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/admin/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (alive) setData(json);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-8 mb-8 flex items-center justify-center"
        data-testid="analytics-loading">
        <Loader2 className="text-[#d9fb06] animate-spin" size={22} />
        <span className="text-[#888680] ml-3 text-sm">Chargement du dashboard…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 text-sm text-red-300"
        data-testid="analytics-error">
        Erreur chargement analytics : {error || 'inconnue'}
      </div>
    );
  }

  const { prospects, revenue, monthly_last_6, utm_sources, boat_types, newsletter, rgpd } = data;

  const maxUtm = Math.max(1, ...utm_sources.map((u) => u.count));
  const maxBoat = Math.max(1, ...boat_types.map((b) => b.count));
  const maxService = Math.max(1, ...revenue.by_service.map((s) => s.cents));

  return (
    <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-6 mb-8" data-testid="analytics-dashboard">
      <div className="flex items-center gap-2 mb-6">
        <TrendingUp className="text-[#d9fb06]" size={22} />
        <h2 className="text-xl font-black text-[#d9fb06] uppercase">Dashboard analytics</h2>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={Users} label="Prospects" value={prospects.total}
          sub={`${prospects.by_status.client || 0} client(s)`} testId="kpi-prospects" />
        <KpiCard icon={Target} label="Taux conv." value={`${prospects.conversion_rate_pct}%`}
          sub={prospects.avg_conversion_days != null ? `~${prospects.avg_conversion_days}j` : 'pas encore de données'}
          testId="kpi-conversion" />
        <KpiCard icon={Euro} label="CA encaissé" value={fmtEUR(revenue.total_cents)}
          sub={`${revenue.transactions} paiement(s)`} testId="kpi-revenue" />
        <KpiCard icon={Mail} label="Newsletter" value={newsletter.subscribers}
          sub={`RGPD ouvertes : ${rgpd.open_requests}`} testId="kpi-newsletter" />
      </div>

      {/* Sparkline 30j */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-[#dfddd6] uppercase tracking-wide">Prospects — 30 derniers jours</h3>
          <span className="text-xs text-[#888680]">total : {prospects.daily_last_30.reduce((s, d) => s + d.count, 0)}</span>
        </div>
        <Sparkline series={prospects.daily_last_30} />
      </div>

      {/* Monthly bars — prospects vs revenue */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-[#dfddd6] uppercase tracking-wide">6 derniers mois</h3>
          <div className="flex gap-3 text-[10px] text-[#888680]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#d9fb06] inline-block rounded-sm" /> Prospects</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#3f4816] inline-block rounded-sm" /> CA</span>
          </div>
        </div>
        <MonthlyChart data={monthly_last_6} />
      </div>

      {/* Two columns: sources & boat types */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-sm font-bold text-[#dfddd6] uppercase tracking-wide mb-3">Sources trafic (UTM)</h3>
          {utm_sources.length === 0 ? (
            <p className="text-xs text-[#888680]">Aucune source détectée.</p>
          ) : (
            <div className="space-y-2" data-testid="utm-sources">
              {utm_sources.map((u) => <BarRow key={u.source} label={u.source} value={u.count} max={maxUtm} />)}
            </div>
          )}
        </div>
        <div>
          <h3 className="text-sm font-bold text-[#dfddd6] uppercase tracking-wide mb-3">Types de bateau demandés</h3>
          {boat_types.length === 0 ? (
            <p className="text-xs text-[#888680]">Pas encore de demande.</p>
          ) : (
            <div className="space-y-2" data-testid="boat-types">
              {boat_types.map((b) => <BarRow key={b.type} label={b.type} value={b.count} max={maxBoat} />)}
            </div>
          )}
        </div>
      </div>

      {/* Revenue by service */}
      {revenue.by_service.length > 0 && (
        <div className="mb-2">
          <h3 className="text-sm font-bold text-[#dfddd6] uppercase tracking-wide mb-3">CA par service</h3>
          <div className="space-y-2" data-testid="revenue-by-service">
            {revenue.by_service.map((s) => (
              <BarRow key={s.service} label={s.service} value={s.cents} max={maxService} valueFmt={fmtEUR} />
            ))}
          </div>
        </div>
      )}

      {/* Status pipeline */}
      <div className="mt-6 pt-4 border-t border-[#3f4816]">
        <h3 className="text-sm font-bold text-[#dfddd6] uppercase tracking-wide mb-3">Pipeline prospects</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {Object.entries(STATUS_LABELS).map(([k, l]) => (
            <div key={k} className="flex justify-between bg-[#1a1c1b] border border-[#3f4816] rounded px-3 py-2">
              <span className="text-[#888680]">{l}</span>
              <span className="text-[#d9fb06] font-bold">{prospects.by_status[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[10px] text-[#888680] mt-4 text-right">
        Généré le {new Date(data.generated_at).toLocaleString('fr-FR')}
      </p>
    </div>
  );
}
